import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Handshake, Plus, MagnifyingGlass, Buildings, LinkSimple, Archive, PencilSimple, CheckCircle, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABELS = {
  private_company: "Private company", government: "Government", ngo: "NGO",
  international_org: "International org", individual: "Individual", other: "Other",
};
const TERM_LABELS = {
  due_on_receipt: "Due on receipt", net_7: "Net 7", net_14: "Net 14", net_30: "Net 30",
  net_45: "Net 45", net_60: "Net 60", milestone: "Per milestone", custom: "Custom",
};

const EMPTY = {
  name: "", client_type: "private_company", registration_no: "", tax_number: "",
  contact_person: "", contact_email: "", contact_phone: "", address: "", country: "Somalia",
  billing_address: "", currency: "USD", payment_terms: "net_30", credit_limit: "",
  tax_treatment: "standard_vat", notes: "",
};

function ClientFormDialog({ open, onClose, onSaved, meta, initial, clientId }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial, credit_limit: initial.credit_limit ?? "" } : EMPTY);
  }, [open, initial]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    setBusy(true);
    try {
      const payload = { ...form, credit_limit: form.credit_limit === "" ? null : parseFloat(form.credit_limit) };
      if (clientId) {
        await api.patch(`/clients/${clientId}`, payload);
        toast.success("Client updated");
      } else {
        const { data } = await api.post("/clients", payload);
        toast.success(`Client "${form.name}" registered — awaiting review`);
        if (data?.projects_auto_linked) {
          toast.info(`${data.projects_auto_linked} existing project(s) with this client name were auto-connected`, { duration: 6000 });
        }
      }
      onClose(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{clientId ? "Edit client" : "Register client"}</DialogTitle>
          <DialogDescription>
            {clientId ? "Changes are audited." : "Essential fields only — billing details are optional below. New clients go through review → activation."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Client name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="client-name-input" /></div>
          <div><Label>Type *</Label>
            <Select value={form.client_type} onValueChange={(v) => set("client_type", v)}>
              <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.client_types || []).map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Country</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
          <div><Label>Contact person</Label><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
          <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></div>
          <div><Label>Contact email</Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></div>
          <div><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
        </div>
        <Accordion type="multiple" className="mt-1">
          <AccordionItem value="billing">
            <AccordionTrigger className="text-sm">Billing & commercial terms (optional)</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 pt-1">
                <div className="sm:col-span-2"><Label>Billing address</Label>
                  <Input value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} placeholder="Defaults to the address above" /></div>
                <div><Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.currencies || ["USD"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Payment terms</Label>
                  <Select value={form.payment_terms} onValueChange={(v) => set("payment_terms", v)}>
                    <SelectTrigger data-testid="client-terms-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.payment_terms || []).map((t) => <SelectItem key={t} value={t}>{TERM_LABELS[t] || t}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Credit limit (USD)</Label>
                  <Input type="number" step="0.01" value={form.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} /></div>
                <div><Label>Tax treatment</Label>
                  <Select value={form.tax_treatment} onValueChange={(v) => set("tax_treatment", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.tax_treatments || []).map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Registration no.</Label><Input value={form.registration_no} onChange={(e) => set("registration_no", e.target.value)} /></div>
                <div><Label>Tax number</Label><Input value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="btn-primary-blue" data-testid="client-save-btn">
            {busy ? "Saving…" : clientId ? "Save changes" : "Register client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{k}</div>
      <div className="text-sm font-medium text-slate-900">{v || "—"}</div>
    </div>
  );
}

function ClientProfile({ clientId, onClose, onChanged, meta, user }) {
  const [p, setP] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [linkPid, setLinkPid] = useState("");

  const load = () => { if (clientId) api.get(`/clients/${clientId}/profile`).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e))); };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!clientId) return null;
  const canEdit = can(user, "clients.edit") ?? true;
  const canDecide = (can(user, "funders.decide") ?? true) || user?.role === "super_admin";

  const decide = async (decision) => {
    let comment = "";
    if (decision !== "approve") {
      comment = window.prompt(`Comment for ${decision}:`) || "";
      if (!comment) return;
    }
    try {
      await api.post(`/funders/${clientId}/decide`, { decision, comment });
      toast.success(`Client ${decision}d`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async () => {
    const reason = window.prompt("Reason for archiving this client:");
    if (!reason) return;
    try {
      await api.post(`/clients/${clientId}/archive`, { reason });
      toast.success("Client archived");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openLink = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(Array.isArray(data) ? data : data.items || []);
      setLinkOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doLink = async () => {
    if (!linkPid) return;
    try {
      await api.post(`/clients/${clientId}/link-project`, { project_id: linkPid });
      toast.success("Project linked");
      setLinkOpen(false); setLinkPid(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="client-profile-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Buildings size={18} className="text-slate-700" weight="duotone" />
            <DialogTitle className="font-display">{p?.name || "Loading…"}</DialogTitle>
            {p && <StatusBadge status={p.status} />}
          </div>
          <DialogDescription>
            {p?.client_code} · {TYPE_LABELS[p?.client_type] || p?.client_type} · {p?.country}
          </DialogDescription>
        </DialogHeader>

        {p && (
          <>
            {p.status === "pending_review" && canDecide && (
              <div className="flex items-center justify-between rounded border border-amber-200 bg-amber-50 p-3 text-xs">
                <span className="text-amber-900 font-medium">Step 1 of 2 — combined Finance/Compliance review pending</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide("approve")} data-testid="client-approve-review">Approve review</Button>
                  <Button size="sm" variant="outline" onClick={() => decide("return")}>Return</Button>
                  <Button size="sm" variant="outline" className="text-red-700" onClick={() => decide("reject")}>Reject</Button>
                </div>
              </div>
            )}
            {p.status === "reviewed" && canDecide && (
              <div className="flex items-center justify-between rounded border border-blue-200 bg-blue-50 p-3 text-xs">
                <span className="text-blue-900 font-medium">Step 2 of 2 — activation pending (reviewer cannot activate — SoD)</span>
                <Button size="sm" onClick={() => decide("approve")} data-testid="client-activate">
                  <CheckCircle size={12} className="mr-1" /> Activate client
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="dc-card p-3"><div className="overline">Invoiced</div>
                <div className="font-display text-lg font-bold tabular-nums">{formatCurrency(p.total_invoiced || 0)}</div></div>
              <div className="dc-card p-3"><div className="overline">Received</div>
                <div className="font-display text-lg font-bold tabular-nums text-emerald-700">{formatCurrency(p.total_received || 0)}</div></div>
              <div className="dc-card p-3"><div className="overline">Outstanding</div>
                <div className="font-display text-lg font-bold tabular-nums text-amber-700" data-testid="client-outstanding">{formatCurrency(p.outstanding || 0)}</div></div>
              <div className="dc-card p-3"><div className="overline">Aging 60+ days</div>
                <div className="font-display text-lg font-bold tabular-nums text-red-700">{formatCurrency(p.aging?.d61_plus || 0)}</div></div>
            </div>

            <Tabs defaultValue="overview" className="mt-1">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="projects" data-testid="client-tab-projects">Projects ({p.projects?.length || 0})</TabsTrigger>
                <TabsTrigger value="invoices" data-testid="client-tab-invoices">Invoices ({p.invoices?.length || 0})</TabsTrigger>
                <TabsTrigger value="receipts">Receipts ({p.receipts?.length || 0})</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="pt-3">
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                  <KV k="Client code" v={p.client_code} />
                  <KV k="Registration no." v={p.registration_no} />
                  <KV k="Tax number" v={p.tax_number} />
                  <KV k="Contact" v={p.contact_person} />
                  <KV k="Phone" v={p.contact_phone} />
                  <KV k="Email" v={p.contact_email} />
                  <KV k="Billing address" v={p.billing_address || p.address} />
                  <KV k="Currency" v={p.currency} />
                  <KV k="Payment terms" v={TERM_LABELS[p.payment_terms] || p.payment_terms} />
                  <KV k="Credit limit" v={p.credit_limit != null ? formatCurrency(p.credit_limit) : "—"} />
                  <KV k="Tax treatment" v={(p.tax_treatment || "").replace(/_/g, " ")} />
                  <KV k="Registered" v={`${formatDate(p.created_at)} by ${p.created_by || "—"}`} />
                </div>
                {p.notes && <p className="mt-3 rounded bg-slate-50 p-3 text-xs text-slate-600">{p.notes}</p>}
              </TabsContent>

              <TabsContent value="projects" className="pt-3">
                {!p.projects?.length ? <EmptyState icon={Buildings} title="No connected projects" description="Link a project or set this client as a project's primary client." /> : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                      <tr><th className="px-3 py-2">Project</th><th className="px-3 py-2 text-right">Contract value</th>
                        <th className="px-3 py-2 text-right">Invoiced</th><th className="px-3 py-2 text-right">Received</th>
                        <th className="px-3 py-2 text-right">Outstanding</th><th className="px-3 py-2">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {p.projects.map((pr) => (
                        <tr key={pr.id} data-testid={`client-project-${pr.id}`}>
                          <td className="px-3 py-2 font-semibold text-slate-900">{pr.name}
                            <div className="text-[10px] text-slate-500">{pr.start_date ? formatDate(pr.start_date) : ""}{pr.end_date ? ` → ${formatDate(pr.end_date)}` : ""} · {pr.progress_pct ?? 0}%</div></td>
                          <td className="px-3 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(pr.budget || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(pr.invoiced || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono-alt tabular-nums text-emerald-700">{formatCurrency(pr.received || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono-alt tabular-nums text-amber-700">{formatCurrency(pr.outstanding || 0)}</td>
                          <td className="px-3 py-2"><StatusBadge status={pr.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              <TabsContent value="invoices" className="pt-3">
                {!p.invoices?.length ? <EmptyState icon={Handshake} title="No invoices" description="Invoices raised against this client will appear here." /> : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                      <tr><th className="px-3 py-2">Invoice</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Due</th>
                        <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {p.invoices.map((inv) => (
                        <tr key={inv.id}>
                          <td className="px-3 py-2 font-mono-alt text-xs">{inv.invoice_number}</td>
                          <td className="px-3 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                          <td className="px-3 py-2 text-xs">{formatDate(inv.due_date)}</td>
                          <td className="px-3 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(inv.total || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono-alt tabular-nums">{formatCurrency(inv.paid || 0)}</td>
                          <td className="px-3 py-2"><StatusBadge status={inv.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              <TabsContent value="receipts" className="pt-3">
                {!p.receipts?.length ? <EmptyState icon={Handshake} title="No receipts" description="Payments received will appear here." /> : (
                  <ul className="divide-y divide-slate-100 text-sm">
                    {p.receipts.map((r, i) => (
                      <li key={i} className="flex items-center justify-between px-2 py-2">
                        <span className="font-mono-alt text-xs">{r.invoice_number} · {formatDate(r.payment_date)}</span>
                        <span className="font-mono-alt tabular-nums font-semibold text-emerald-700">{formatCurrency(r.amount || 0)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="history" className="pt-3">
                {!p.audit_history?.length ? <div className="text-xs text-slate-400 py-4 text-center">No history yet.</div> : (
                  <ul className="divide-y divide-slate-100 text-xs">
                    {p.audit_history.map((a, i) => (
                      <li key={i} className="px-2 py-2">
                        <span className="font-mono-alt uppercase tracking-wider text-slate-600">{a.action}</span>
                        <span className="text-slate-400"> · {a.actor_email} · {a.created_at ? formatDate(a.created_at) : ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {canEdit && <Button variant="outline" size="sm" onClick={openLink} data-testid="client-link-project-btn"><LinkSimple size={12} className="mr-1.5" /> Link project</Button>}
          {canEdit && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="client-edit-btn"><PencilSimple size={12} className="mr-1.5" /> Edit</Button>}
          {canEdit && p?.status !== "archived" && <Button variant="outline" size="sm" onClick={archive} data-testid="client-archive-btn"><Archive size={12} className="mr-1.5" /> Archive</Button>}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>

        <ClientFormDialog open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => { load(); onChanged?.(); }} meta={meta} initial={p} clientId={clientId} />
        <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display">Link project to {p?.name}</DialogTitle></DialogHeader>
            <Select value={linkPid} onValueChange={setLinkPid}>
              <SelectTrigger data-testid="client-link-project-select"><SelectValue placeholder="Pick a project" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {projects.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <DialogFooter><Button onClick={doLink} disabled={!linkPid} data-testid="client-link-confirm">Link</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const canCreate = can(user, "clients.create") ?? true;

  const load = () => {
    setLoading(true);
    api.get("/clients").then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/clients/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => items.filter((c) => {
    if (status !== "all" && c.status !== status) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [c.name, c.client_code, c.funder_no, c.contact_email, c.contact_phone]
      .some((v) => (v || "").toLowerCase().includes(s));
  }), [items, search, status]);

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="Client master"
        title="Clients"
        description="Who the company works for — connected to projects, invoices and receivables. Part of the Funders register."
        actions={canCreate && (
          <Button className="btn-primary-blue" onClick={() => setCreating(true)} data-testid="client-register-btn">
            <Plus size={14} className="mr-2" /> Register client
          </Button>
        )}
      />
      <div className="p-6 sm:p-8 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code, contact…"
              className="w-64 pl-9" data-testid="client-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="client-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {["pending_review", "reviewed", "active", "returned", "rejected", "archived"].map((s) =>
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-slate-500">{filtered.length} client(s)</span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Handshake} title="No clients yet" description="Register your first client — they will flow into projects and invoicing." />
        ) : (
          <div className="dc-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Client</th>
                  <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Contact</th>
                  <th className="px-4 py-2.5">Terms</th><th className="px-4 py-2.5 text-right">Outstanding</th>
                  <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedId(c.id)} data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-2.5 font-mono-alt text-xs">{c.client_code || c.funder_no}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-900">{c.name}
                      <div className="text-[10px] font-normal text-slate-500">{c.country}</div></td>
                    <td className="px-4 py-2.5 text-xs">{TYPE_LABELS[c.client_type] || c.client_type || "—"}</td>
                    <td className="px-4 py-2.5 text-xs">{c.contact_person || "—"}
                      <div className="text-[10px] text-slate-500">{c.contact_email}</div></td>
                    <td className="px-4 py-2.5 text-xs">{TERM_LABELS[c.payment_terms] || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums">{formatCurrency(c.outstanding || 0)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-2.5 text-right"><ArrowUpRight size={13} className="text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClientFormDialog open={creating} onClose={() => setCreating(false)} onSaved={load} meta={meta} />
      <ClientProfile clientId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} meta={meta} user={user} />
    </div>
  );
}
